import React, { useState, useCallback } from "react";
import { ArrowLeft, Loader2, Mail, MailCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormField } from "@/components/ui/form-field";
import {
  EMAIL_FORMAT_REGEX,
  INVALID_EMAIL_FORMAT_ERROR,
  NEW_USER_LOGIN_PAGE,
  NIMIS_WEBSITE_URL,
} from "@/constants/definitions";
import { requestNewInvite } from "@/services/requestInvite.service";

// Neutral confirmation shown UNCONDITIONALLY after submit — identical whether or
// not the account exists, so this page can never be used to enumerate users.
const CONFIRMATION_HEADING = "Check your email";
const CONFIRMATION_BODY =
  "If a pending account exists for that address, a new invitation with a temporary passphrase has been sent. It can take a few minutes to arrive — remember to check your spam folder.";

interface RequestInviteProps {
  navigate: (path: string) => void;
}

export default function RequestInvite({ navigate }: RequestInviteProps) {
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = useCallback(async () => {
    setErrorMessage("");

    if (!email) return;

    // Format check only — reveals nothing about account existence.
    if (!EMAIL_FORMAT_REGEX.test(email)) {
      setErrorMessage(INVALID_EMAIL_FORMAT_ERROR);
      return;
    }

    try {
      setIsLoading(true);
      await requestNewInvite(email);
    } finally {
      // Always land on the same neutral confirmation regardless of outcome. The
      // service never throws, but guard here too to preserve no-enumeration.
      setIsLoading(false);
      setSubmitted(true);
    }
  }, [email]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter") handleSubmit();
    },
    [handleSubmit]
  );

  return (
    <div data-theme="dark" className="relative" style={{ background: "hsla(222, 70%, 8%, 0.85)", minHeight: "calc(100vh + 1px)" }}>
      <div className="pointer-events-none absolute inset-0" style={{
        background: "radial-gradient(ellipse 60% 50% at 20% 30%, rgba(59,130,246,0.08) 0%, transparent 70%), radial-gradient(ellipse 50% 40% at 80% 70%, rgba(14,165,233,0.05) 0%, transparent 70%)",
      }} />

      <div className="relative z-10 mx-auto flex w-full max-w-6xl items-center px-4 sm:px-6 pt-6 pb-6">
        <a href={NIMIS_WEBSITE_URL}>
          <img src="/nimis-intelligence-white-logo.png" alt="NIMIS Intelligence" className="h-12 w-auto" />
        </a>
      </div>

      <div className="relative z-10 flex justify-center pt-[12vh]">
        <div className="w-full max-w-sm mx-4">
          <div className="rounded-xl bg-white/[0.03] backdrop-blur-sm border border-white/[0.08] p-8">
            {submitted ? (
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-500/10 border border-blue-500/20">
                  <MailCheck className="h-6 w-6 text-blue-400" />
                </div>
                <h1 className="text-xl font-semibold text-white">{CONFIRMATION_HEADING}</h1>
                <p className="mt-2 text-sm text-white/50 leading-relaxed">{CONFIRMATION_BODY}</p>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <h1 className="text-xl font-semibold text-white">Request a new invite</h1>
                  <p className="mt-1.5 text-sm text-white/50">
                    Enter your email and we'll send a fresh invitation if a pending account exists.
                  </p>
                </div>

                {errorMessage && (
                  <div className="mb-4 rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-300">
                    {errorMessage}
                  </div>
                )}

                <div className="space-y-4">
                  <FormField label="Email" htmlFor="email" variant="dark">
                    <Input
                      id="email"
                      type="email"
                      variant="dark"
                      placeholder="name@company.com"
                      value={email}
                      onChange={(changeEvent) => { setEmail(changeEvent.target.value); setErrorMessage(""); }}
                      onKeyDown={handleKeyDown}
                      autoComplete="email"
                      disabled={isLoading}
                      leftIcon={<Mail className="h-4 w-4" />}
                    />
                  </FormField>

                  <Button
                    onClick={handleSubmit}
                    disabled={isLoading || !email}
                    className="w-full h-10 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg transition-colors disabled:bg-blue-600/50"
                  >
                    {isLoading ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending invite...</>
                    ) : (
                      "Send new invite"
                    )}
                  </Button>
                </div>
              </>
            )}

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => navigate(NEW_USER_LOGIN_PAGE)}
                className="inline-flex items-center gap-1.5 text-sm text-white/40 hover:text-white/70 transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
